import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
  Paper,
} from '@mui/material';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import { useBookmarks } from '../contexts/BookmarkContext';
import courseService from '../services/courseService';
import type { Course } from '../services/courseService';
import { CourseType } from '../services/courseService';
import { getFieldsOfStudy } from '../utils/fieldOfStudy';

export default function Bookmarks() {
  const { bookmarkedIds, toggle } = useBookmarks();
  const [allCourses, setAllCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    courseService.getCourses().then((data) => {
      setAllCourses(data);
      setLoading(false);
    });
  }, []);

  const bookmarkedCourses = allCourses.filter((c) => bookmarkedIds.has(c.id));

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 12 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <BookmarkIcon sx={{ fontSize: 32, color: 'primary.main' }} />
        <Box>
          <Typography variant="h5" fontWeight={500}>My Bookmarks</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
            Courses you've saved for later
          </Typography>
        </Box>
        <Box sx={{ flex: 1 }} />
        <Typography variant="body2" color="text.secondary">
          {bookmarkedCourses.length} {bookmarkedCourses.length === 1 ? 'course' : 'courses'}
        </Typography>
      </Box>

      {bookmarkedCourses.length === 0 ? (
        /* Empty state */
        <Paper
          elevation={0}
          sx={{
            p: 8,
            textAlign: 'center',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: '8px',
          }}
        >
          <BookmarkBorderIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" fontWeight={500} sx={{ mb: 1 }}>
            No bookmarks yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Browse courses and click the bookmark icon to save them here.
          </Typography>
          <Button variant="contained" component={Link} to="/app/courses">
            Browse Courses
          </Button>
        </Paper>
      ) : (
        /* Bookmarked courses grid */
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', lg: 'repeat(4, 1fr)' },
            gap: 2,
          }}
        >
          {bookmarkedCourses.map((course) => (
            <Card
              key={course.id}
              elevation={0}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: '8px',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <CardContent sx={{ p: 2, flex: 1, display: 'flex', flexDirection: 'column', '&:last-child': { pb: 2 } }}>
                {/* Type + bookmark */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Chip
                    label={course.courseType === CourseType.Webinar ? 'Webinar' : 'On-Demand'}
                    size="small"
                    variant="outlined"
                    sx={{ height: '22px', fontSize: '12px', borderRadius: '100px' }}
                  />
                  <Tooltip title="Remove bookmark">
                    <IconButton
                      size="small"
                      onClick={() => toggle(course.id)}
                      sx={{ color: 'primary.main' }}
                      aria-label="Remove bookmark"
                    >
                      <BookmarkIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>

                {/* Title */}
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 500,
                    fontSize: '15px',
                    lineHeight: 1.4,
                    mb: 1,
                    flex: 1,
                    overflow: 'hidden',
                    display: '-webkit-box',
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical',
                  }}
                >
                  {course.title}
                </Typography>

                {/* Meta */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1.5 }}>
                  {getFieldsOfStudy(course)[0] && (
                    <Chip
                      icon={<MenuBookIcon sx={{ fontSize: '14px !important' }} />}
                      label={getFieldsOfStudy(course)[0]}
                      size="small"
                      variant="outlined"
                      sx={{ height: '22px', fontSize: '12px', borderRadius: '100px' }}
                    />
                  )}
                  <Chip
                    icon={<AccessTimeIcon sx={{ fontSize: '14px !important' }} />}
                    label={`${course.creditHours} CPE`}
                    size="small"
                    variant="outlined"
                    sx={{ height: '22px', fontSize: '12px', borderRadius: '100px' }}
                  />
                </Box>

                {/* CTA */}
                <Button
                  variant="outlined"
                  size="small"
                  component={Link}
                  to={`/app/courses/${course.id}`}
                  endIcon={<ArrowForwardIcon sx={{ fontSize: '14px !important' }} />}
                  sx={{
                    textTransform: 'uppercase',
                    fontSize: '13px',
                    letterSpacing: '0.46px',
                    fontWeight: 500,
                  }}
                >
                  {course.courseType === CourseType.Webinar ? 'View' : 'Enroll'}
                </Button>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}
    </Box>
  );
}
